import Web3Modal, { ICoreOptions } from 'web3modal';
import WalletConnectProvider from '@walletconnect/web3-provider';
import { ethers, utils } from 'ethers';
import { ChainId } from '../../../ChainId';
import { ArnConnector } from '@arianee/arn-types';

export class Web3ModalConnector implements ArnConnector {
  protected readonly web3Modal: Web3Modal;

  isConnected = false;
  provider?: ethers.providers.Web3Provider;

  protected log(..._data: any[]) {
    // console.log(`[${this.constructor.name}]`, ...data);
  }

  constructor(options: Partial<ICoreOptions> = {}) {
    // It may also be useful to inject other options such as RPCs to avoid having to specify them in the various configurations
    for (const option in options.providerOptions) {
      switch (option) {
        case 'walletconnect': {
          options.providerOptions[option].package = WalletConnectProvider;
          options.providerOptions[option].options = {
            ...options.providerOptions[option].options,
            rpc: {
              [ChainId.PoaNetworkSokol]: 'https://sokol.arianee.net',
              [ChainId.PoaNetworkCore]: 'https://poa.arianee.net',
              [ChainId.PolygonMainnet]: 'https://polygon.arianee.net',
            },
          };
        }
      }
    }
    const constructor = (Web3Modal as any)?.default ?? Web3Modal;
    this.web3Modal = new constructor({ cacheProvider: true, ...options });
  }

  async connect(): Promise<void> {
    if (this.isConnected) {
      this.log('Already connected'); // TODO: Throw error
      return;
    }
    if (this.web3Modal.cachedProvider) {
      this.log('Using cached provider', this.web3Modal.cachedProvider);
    } else {
      this.log('No cached provider found');
    }
    const web3Provider = await this.web3Modal.connect();
    if (!web3Provider) {
      throw Error('Provider not activated');
    }
    this.provider = new ethers.providers.Web3Provider(web3Provider);
    this.isConnected = true;
    this.log('Connected');
  }

  async onConnectFromCache(): Promise<void> {
    // NOP
  }

  async signMessage(message: string): Promise<any> {
    await this.checkConnected();
    const signature = await this.provider!.getSigner().signMessage(message);
    this.log('Signed: { message: "%s", signature: "%s" }', message, signature);
    return signature;
  }

  signTypedData(
    _domain: ethers.TypedDataDomain,
    _types: Record<string, ethers.TypedDataField[]>,
    _value: Record<string, any>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  signTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  sendTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<ethers.providers.TransactionResponse> {
    throw new Error('Method not implemented');
  }

  sendUncheckedTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  protected async checkConnected() {
    if (!this.isConnected) {
      await this.connect();
    }
  }

  public async getChainId(): Promise<number> {
    throw new Error('Method not implemented');
  }

  public async switchChain(chainId: number): Promise<void> {
    await this.checkConnected();
    // TODO: Check if the provider already know the chainId, if not use the wallet_addEthereumChain method to add it
    // https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
    await this.provider!.send('wallet_switchEthereumChain', [
      { chainId: utils.hexlify(chainId) },
    ]);
  }

  async disconnect(): Promise<void> {
    this.web3Modal.clearCachedProvider();
    // Remove WalletConnect session from local storage
    localStorage.removeItem('walletconnect');
    this.isConnected = false;
    this.provider = undefined;
    this.log('Disconnected');
  }
}
